import { AxiosResponse } from "axios";
import { all, call, put, takeEvery } from "redux-saga/effects";
import api from "../../api";
import { PageSize } from "../../shared/constant";
import Endpoint from "../../shared/endpoints";
import trackConstants from "../constants/trackConstants";
import globalUtils from "../../utils/globalUtils";

const getTracks = (payload: any) =>
  api.get(
    // eslint-disable-next-line no-nested-ternary
    payload.catalog
      ? `${Endpoint.retrieveTracks}/${payload.catalog}/`
      : payload.clapReport
      ? `${Endpoint.retrieveTracks}/${payload.clapReport}/`
      : `${Endpoint.retrieveTracks}/`,
    {
      params: {
        ...(payload.clientId && { client: payload.clientId }),
        page: payload.page,
        ...(payload.searchText && { search: payload.searchText }),
        ...(payload.labels &&
          payload.labels.length && { labels: payload.labels?.join(", ") }),
        ...(payload.artist &&
          payload.artist.length && { artist: payload.artist?.join(", ") }),
        ...(payload.lyricist &&
          payload.lyricist.length && {
            lyricists: payload.lyricist?.join(", "),
          }),
        ...(payload.composer &&
          payload.composer.length && {
            composers: payload.composer?.join(", "),
          }),
        ...(payload.track &&
          payload.track.length && { track: payload.track?.join(", ") }),
        ...(payload.platform &&
          payload.platform.length && {
            platform: payload.platform?.join(", "),
          }),
        ...(payload.distributor &&
          payload.distributor.length && {
            distributor: payload.distributor?.join(", "),
          }),
        ...(payload.currency && { currency: payload.currency }),
        ...(payload.dateRange &&
          payload.dateRange.from && {
            period_range: `${payload.dateRange.from}, ${payload.dateRange.to}`,
          }),
        ...(payload.clientPayableSort && {
          ordering: payload.clientPayableSort,
        }),
        page_size: PageSize.FIFTY,
      },
    },
  );

const getTracksIncomeSummary = (payload: any) =>
  api.get(Endpoint.retrieveTracksIncomeSummary, {
    params: {
      ...(payload.reason && { reason: payload.reason }),
      ...(payload.clientId && { client: payload.clientId }),
      page: payload.page,
      ...(payload.labels &&
        payload.labels.length && { labels: payload.labels?.join(", ") }),
      ...(payload.artist &&
        payload.artist.length && { artist: payload.artist?.join(", ") }),
      ...(payload.lyricist &&
        payload.lyricist.length && {
          lyricists: payload.lyricist?.join(", "),
        }),
      ...(payload.composer &&
        payload.composer.length && {
          composers: payload.composer?.join(", "),
        }),
      ...(payload.track &&
        payload.track.length && { track: payload.track?.join(", ") }),
      ...(payload.platform &&
        payload.platform.length && {
          platform: payload.platform?.join(", "),
        }),
      ...(payload.distributor &&
        payload.distributor.length && {
          distributor: payload.distributor?.join(", "),
        }),
      ...(payload.currency && { currency: payload.currency }),
      ...(payload.dateRange &&
        payload.dateRange.from && {
          period_range: `${payload.dateRange.from}, ${payload.dateRange.to}`,
        }),
    },
  });

const downloadTracksInCSV = (payload: any) =>
  api.get(Endpoint.downloadTracksInCSV, {
    responseType: "blob",
    params: {
      ...(payload.clientId && { client: payload.clientId }),
      ...(payload.csv_type && { csv_type: payload.csv_type }),
      ...(payload.labels &&
        payload.labels.length && { labels: payload.labels?.join(", ") }),
      ...(payload.artist &&
        payload.artist.length && { artist: payload.artist?.join(", ") }),
      ...(payload.lyricist &&
        payload.lyricist.length && {
          lyricists: payload.lyricist?.join(", "),
        }),
      ...(payload.composer &&
        payload.composer.length && {
          composers: payload.composer?.join(", "),
        }),
      ...(payload.track &&
        payload.track.length && { track: payload.track?.join(", ") }),
      ...(payload.platform &&
        payload.platform.length && {
          platform: payload.platform?.join(", "),
        }),
      ...(payload.distributor &&
        payload.distributor.length && {
          distributor: payload.distributor?.join(", "),
        }),
      ...(payload.currency && { currency: payload.currency }),
      ...(payload.dateRange &&
        payload.dateRange.from && {
          period_range: `${payload.dateRange.from}, ${payload.dateRange.to}`,
        }),
    },
  });

const getAllTracks = (payload: any) =>
  api.get(Endpoint.retrieveAllTracks, {
    params: {
      ...(payload.clientId && { client: payload.clientId }),
      ...(payload.label && { label_name: payload.label }),
      ...(payload.artist && { artist: payload.artist }),
    },
  });

const createTrack = (payload: any) => api.post(Endpoint.createTrack, payload);
const editTrack = (payload: any) =>
  api.patch(`${Endpoint.editTrack}/${payload.id}/`, payload.body);

const deleteTrack = (payload: any) =>
  api.delete(`${Endpoint.deleteTrack}/${payload.id}/`);

function* getTracksAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(getTracks, action.payload);

    yield put({
      type: trackConstants.GET_TRACKS_LIST_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: trackConstants.GET_TRACKS_LIST_FAILURE, err: e });
  }
}

function* downloadTracksInCSVAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(
      downloadTracksInCSV,
      action.payload,
    );

    globalUtils.download(response.data, "clap-reports.csv");

    yield put({
      type: trackConstants.DOWNLOAD_TRACKS_IN_CSV_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: trackConstants.DOWNLOAD_TRACKS_IN_CSV_FAILURE, err: e });
  }
}

function* getAllTracksAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(getAllTracks, action.payload);

    yield put({
      type: trackConstants.GET_ALL_TRACKS_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: trackConstants.GET_ALL_TRACKS_FAILURE, err: e });
  }
}

function* getTracksIncomeSummaryAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(
      getTracksIncomeSummary,
      action.payload,
    );

    yield put({
      type: trackConstants.GET_TRACKS_INCOME_SUMMARY_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({
      type: trackConstants.GET_TRACKS_INCOME_SUMMARY_FAILURE,
      err: e,
    });
  }
}

function* createTrackAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(createTrack, action.payload);

    yield put({
      type: trackConstants.CREATE_TRACK_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: trackConstants.CREATE_TRACK_FAILURE, err: e });
  }
}

function* editTrackAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(editTrack, action.payload);

    yield put({
      type: trackConstants.EDIT_TRACK_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: trackConstants.EDIT_TRACK_FAILURE, err: e });
  }
}

function* deleteTrackAsync(action: any) {
  try {
    const response: AxiosResponse = yield call(deleteTrack, action.payload);

    yield put({
      type: trackConstants.DELETE_TRACK_SUCCESS,
      payload: response,
    });
  } catch (e) {
    yield put({ type: trackConstants.DELETE_TRACK_FAILURE, err: e });
  }
}

function* trackSaga() {
  yield all([takeEvery(trackConstants.GET_TRACKS_LIST, getTracksAsync)]);
  yield all([
    takeEvery(
      trackConstants.DOWNLOAD_TRACKS_IN_CSV_REQUEST,
      downloadTracksInCSVAsync,
    ),
  ]);
  yield all([
    takeEvery(trackConstants.GET_ALL_TRACKS_REQUEST, getAllTracksAsync),
  ]);
  yield all([
    takeEvery(
      trackConstants.GET_TRACKS_INCOME_SUMMARY_REQUEST,
      getTracksIncomeSummaryAsync,
    ),
  ]);
  yield all([takeEvery(trackConstants.CREATE_TRACK_REQUEST, createTrackAsync)]);
  yield all([takeEvery(trackConstants.EDIT_TRACK_REQUEST, editTrackAsync)]);
  yield all([takeEvery(trackConstants.DELETE_TRACK_REQUEST, deleteTrackAsync)]);
}

export default trackSaga;
